import { MessageCircle, Users, BedDouble, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { ROOMS, waLink } from "../data";
import { useLang } from "../i18n";

const RoomCard = ({ room, span, tall }) => {
  const { s } = useLang();
  const tr = s.roomData[room.id];
  return (
  <Reveal className={span}>
    <article
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gold/15 bg-[#121E4A]"
      data-testid={`room-card-${room.id}`}
    >
      <div className={`relative overflow-hidden ${tall ? "aspect-[16/10]" : "aspect-[16/11]"}`}>
        <img
          src={room.image}
          alt={`${room.name} — ${room.type} at Shri Jamuna Dham, Vrindavan`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1230] via-transparent to-transparent" />
        <span className="absolute top-4 left-4 rounded-full bg-cream/90 px-4 py-1.5 text-xs font-semibold tracking-wide text-maroon backdrop-blur">
          {s.rooms.price}
        </span>
        <span className="absolute bottom-4 right-5 font-hindi text-3xl text-cream/90 drop-shadow-lg">
          {room.hindi}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-saffron font-semibold">{tr.type}</p>
        <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold text-cream tracking-tight">
          {room.name}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-cream/70">{tr.blurb}</p>

        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-cream/80">
          <span className="flex items-center gap-2">
            <Users size={15} className="text-gold" /> {tr.capacity}
          </span>
          <span className="flex items-center gap-2">
            <BedDouble size={15} className="text-gold" /> {tr.beds}
          </span>
        </div>

        <ul className="mt-5 flex flex-wrap gap-2">
          {tr.features.map((f) => (
            <li
              key={f}
              className="flex items-center gap-1.5 rounded-full border border-cream/15 px-3 py-1 text-xs text-cream/75"
            >
              <Check size={12} className="text-saffron" /> {f}
            </li>
          ))}
        </ul>

        <div className="mt-7 flex-1" />
        <a
          href={waLink(
            `Namaste! I'm interested in the ${room.name} (${room.type}) at Shri Jamuna Dham, Vrindavan. Please share the tariff and availability.`
          )}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`room-enquire-${room.id}`}
          className="inline-flex w-fit items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-semibold text-night transition-colors duration-300 hover:bg-gold"
        >
          <MessageCircle size={16} /> {s.rooms.enquire}
        </a>
      </div>
    </article>
  </Reveal>
  );
};

export const Rooms = () => {
  const { s } = useLang();
  return (
  <section id="rooms" className="relative bg-night py-24 sm:py-32 grain" data-testid="rooms-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold" data-testid="rooms-overline">
          {s.rooms.overline}
        </p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-cream tracking-tight max-w-2xl" data-testid="rooms-heading">
            {s.rooms.heading}
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-cream/60">
            {s.rooms.intro}
          </p>
        </div>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <RoomCard room={ROOMS[0]} span="lg:col-span-7" tall />
        <RoomCard room={ROOMS[1]} span="lg:col-span-5" tall />
        <RoomCard room={ROOMS[2]} span="lg:col-span-12" />
      </div>
    </div>
  </section>
  );
};
